/**
 * MatchdayBoostModal — iter87.5. Brutalist-centered dialog matching
 * the Power Up shell + status pill + Chip Save Autoclose.
 * iter87.59 — motion via shared BentoModal (spring in / mirrored
 * out, backdrop blur ramp).
 */
import { useState } from "react";
import { ChevronRight, Flag } from "lucide-react";
import MatchdayBoostPanel from "@/components/dashboard/MatchdayBoostPanel";
import PlayoffPredictorPanel from "@/components/dashboard/PlayoffPredictorPanel";
import { todoStatusFor, playoffPredictorDisplayStatus } from "@/lib/todoStatus";
import ChipStatusPill from "@/components/onui/ChipStatusPill";
import { BentoModal } from "@/components/onui/BentoMotion";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";

export default function MatchdayBoostModal({
    open, onOpenChange, tournament, me, boostState, onRefresh,
}) {
    // iter87.380 (user-requested) — floating header/footer shell to
    // match the Champion pick modals: header sits flush against the
    // top border, content scrolls independently, and the save footer
    // (rendered by MatchdayBoostPanel via this portal slot) sits flush
    // against the bottom border instead of scrolling with the picks.
    const [footerSlot, setFooterSlot] = useState(null);
    if (!tournament || !me) return null;
    const status = todoStatusFor("boost", { tournament, me, boost: { state: boostState } });
    const tpl = tournament?.template;
    // iter87.558 (user-requested reversal of iter87.440) — NFL/CFB/
    // NWSL Playoff Predictor now shows its real Open/Live color;
    // MLS keeps the forced-gray treatment here (still mid its own
    // iter87.465.24 phased gate).
    const displayStatus = playoffPredictorDisplayStatus(tpl, status);
    // iter87.115 — Boost-only football template (nfl_boost_api)
    // renders as "Gameday Picks" — Boost IS the whole
    // tournament. iter87.122 — the NFL Season Pick'em template
    // (nfl_season_api) uses Boost as a sidecar, so it renders as
    // "Gameday Boost" to stay on-brand while signaling this is the
    // in-season boost surface, not the primary picks. iter87.199.169
    // — Soccer Weekly Spread boost-only (epl/la_liga_boost_api) is
    // ALSO the whole game — renders as "Matchday Picks", not
    // "Matchday Boost" (reserved for the club-season sidecar).
    const isBoostOnlyFootball = tpl === "nfl_boost_api";
    const isBoostOnlySoccer = tpl === "epl_boost_api" || tpl === "la_liga_boost_api";
    const isSeasonFootball = tpl === "nfl_season_api";
    // iter87.465.22 (user-reported) — MLS Conf Semis+ reuses this same
    // shell, same fix as the `isFootballPlayoffsTemplate` check in
    // MatchdayBoostPanel.jsx below — was falling through to the
    // generic "Matchday Boost" title + club-season pot copy.
    const isPlayoffsTemplate = isFootballPlayoffsTemplate(tpl) || tpl === "mls_playoffs_api";
    const modalTitle = isBoostOnlyFootball
        ? "Gameday Picks"
        : isBoostOnlySoccer
            ? "Matchday Picks"
            : isSeasonFootball
                ? "Gameday Boost"
                : isPlayoffsTemplate
                    // iter87.370 — Playoff Predictor: spread pick + a
                    // new Over/Under pick, per playoff game.
                    // iter87.465.25 (user-requested) — MLS now shares
                    // the same "Playoff Predictor" name as NFL/CFB.
                    ? "Playoff Predictor"
                    : "Matchday Boost";

    const handleSaved = () => {
        onRefresh?.();
        setTimeout(() => onOpenChange?.(false), 220);
    };
    // iter87.467 — Playoff Predictor (MLB-shaped, all rounds visible
    // at once) stays open after Save, same as MlbSeriesPickModal /
    // UclAdvancePickModal — there's always more to do (other open
    // games, or just browsing Completed), unlike the old single-round
    // Weekly Boost flow which closed after its one Save.
    const handlePlayoffSaved = () => onRefresh?.();

    return (
        <BentoModal
            open={open}
            onClose={() => onOpenChange?.(false)}
            testId="matchday-boost-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName={`brutal-card max-w-3xl w-full max-h-[90dvh] overflow-hidden flex flex-col ${displayStatus === "pending" ? "border-white/20" : "border-volt"}`}
        >
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/[0.08] shrink-0">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-8 h-8 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                        <Flag className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate min-w-0">{modalTitle}</h3>
                    <ChipStatusPill
                        status={displayStatus}
                        testId="matchday-boost-modal-status"
                        /* iter87.115 — When football tournament is "open"
                           only because the schedule for the current week
                           hasn't been published yet (no fixtures on record),
                           show a friendlier "Schedule TBD" instead of the
                           generic "OPEN" pill.
                           iter87.199.52 — Predicate updated to check
                           `status === "open"` (boost never returns "locked"
                           anymore per operator directive — see
                           `lib/todoStatus.js`). */
                        label={
                            status === "open"
                                && (isBoostOnlyFootball || isSeasonFootball)
                                && !(boostState?.fixtures || []).length
                                ? "Schedule TBD"
                                : undefined
                        }
                    />
                </div>
                <button
                    onClick={() => onOpenChange?.(false)}
                    className="brutal-btn-ghost p-2 shrink-0"
                    data-testid="close-matchday-boost-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                {isPlayoffsTemplate ? (
                    <PlayoffPredictorPanel
                        tournament={tournament}
                        me={me}
                        onRefresh={handlePlayoffSaved}
                        footerSlot={footerSlot}
                    />
                ) : (
                    <MatchdayBoostPanel
                        tournament={tournament}
                        me={me}
                        state={boostState}
                        onRefresh={handleSaved}
                        footerSlot={footerSlot}
                    />
                )}
            </div>
            <div ref={setFooterSlot} className="shrink-0" />
        </BentoModal>
    );
}
